import { createContext, useEffect, useReducer } from 'react';

// router
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

// redux
import { useDispatch } from 'react-redux';

import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';
import {
    ACCESS_TOKEN,
    OIDC_NEXT_URL,
    OPEN_ICIVET_AUTHORIZE_DIALOG,
    PROJECT, USER_AREA_LIST, USER_DEFAULT_AREA, USER_LOGIN_METHOD, USER_NAME,
    I18_NEXT_LNG,
    WEBSITE_ACTIVE_FLAG,
} from '../store/types';
import { getStorage, LocalStorage, removeStorage, setStorage } from '../utils/storage';
import {
    getCmsData,
    queryDashboardBanner,
    queryFriendlyLinks,
    queryPermissionWebsiteList,
    queryRecommend,
    queryWebsiteList,
    setUserInformation,
} from '../store/actions/userActions';
import { queryUnreadNotificationTotal } from '../store/actions/slices/notificationActions';

import axios from '../_db/http/axios.service';
import {
    smsFactorValidate,
    emailFactorValidate, querySignVisit, queryProfile2,
} from '../_db/api.service';
import { getAction, postAction, postRequest } from '../_db/http/http.service';
import { toastError, toastSuccess } from '../components/toastify';
import { useRouter } from '../hooks/useRoutes';
import { resetStore } from '../store';

const initialAuthState = {
    isAuthenticated: false,
    isInitialized: false,
    profile: null,
    worker: null,
    factor: null,
};

const setSession = (accessToken) => {
    if (accessToken) {
        setStorage(ACCESS_TOKEN, accessToken);
        // axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        axios.defaults.headers.common.Authorization = `${accessToken}`;
    } else {
        removeStorage(ACCESS_TOKEN);
        delete axios.defaults.headers.common.Authorization;
    }
};


const handlers = {
    INITIALIZE: (state, action) => {
        const { isAuthenticated, profile, worker } = action.payload;

        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            profile,
            worker
        };
    },
    FACTOR: (state, action) => {
        const { factor } = action.payload;
        return {
            ...state,
            factor,
        };
    },
    LOGIN: (state, action) => {
        const { profile, worker } = action.payload;
        return {
            ...state,
            isAuthenticated: true,
            profile,
            worker,
        };
    },
    LOGOUT: (state) => ({
        ...state,
        isAuthenticated: false,
        profile: null
    }),
    REGISTER: (state, action) => {
        const { profile } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            profile
        };
    }
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
    ...initialAuthState,
    signIn: () => Promise.resolve(),
    smsIn: () => Promise.resolve(),
    emailIn: () => Promise.resolve(),
    ssoIn: () => Promise.resolve(),
    logOut: () => Promise.resolve(),
    signOut: () => Promise.resolve(),
    register: () => Promise.resolve(),
    signUp: () => Promise.resolve(),
    updateProfileInformation: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
    const { t, i18n } = useTranslation();

    const { search, pathname } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    // 判斷是否登錄后重定向
    const redirectUrl = searchParams.get('redirect')?.trim();

    const navigator = useNavigate();
    const dispatch = useDispatch();

    const { children } = props;
    const [state, commit] = useReducer(reducer, initialAuthState);


    useEffect(() => {
        (async function() {
            try {
                const accessToken = getStorage(ACCESS_TOKEN);
                if (accessToken) {
                    setSession(accessToken);
                    const { profile, worker, factor } = await getProfileInformation(getStorage(USER_NAME), getStorage(USER_LOGIN_METHOD), getStorage(USER_DEFAULT_AREA), getStorage(USER_AREA_LIST));
                    commit({
                        type: 'INITIALIZE',
                        payload: {
                            isAuthenticated: true,
                            profile,
                            worker,
                            factor
                        }
                    });
                } else {
                    dispatch(getCmsData());
                    commit({
                        type: 'INITIALIZE',
                        payload: {
                            isAuthenticated: false,
                            profile: null,
                            worker: null
                        }
                    });
                }
            } catch (err) {
                console.error(err);
                commit({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: false,
                        profile: null,
                        worker: null
                    }
                });
            }
        })();
    }, []);

    // 個人資料(賬戶資料+員工資料+應用收藏)
    const getProfileInformation = async (userName, loginMethod, area, areaList) => {
        await setStorage(USER_NAME, userName);
        await setStorage(USER_LOGIN_METHOD, loginMethod);
        await setStorage(USER_DEFAULT_AREA, area);
        await setStorage(USER_AREA_LIST, areaList);

        // 判斷當前語言是否在當前站點語言選單中
        if(!area?.areaLanguageCode?.split(',')?.includes(LocalStorage.get(I18_NEXT_LNG))) {
            LocalStorage.set(I18_NEXT_LNG, area?.defaultLanguageCode);
            i18n?.changeLanguage(area?.defaultLanguageCode);
        };
        // 獲取站點
        // await dispatch(queryWebsiteList());
        await dispatch(queryPermissionWebsiteList(area, areaList));
        // 查詢訪問統計
        // const { result: { all, today } } = await querySignVisit();

        // 是否存在未讀消息通知
        await dispatch(queryUnreadNotificationTotal());

        // 查詢賬戶資料
        const { result: user } = await queryProfile2({ userName });
        const { employee } = user || {};


        // 設置個人資料數據
        await commit({
            type: 'LOGIN',
            payload: {
                profile: user,
                worker: employee
            }
        });
        // 賬戶資料+員工資料 // today_visitor_number: today, total_readers_number: all
        await dispatch(setUserInformation({ profile: user, worker: employee }));

        // 首頁數據
        await updateDashboardData();

        return { profile: user, worker: employee };
    };

    const signIn = async (username, password, captcha, checkKey) => {
        const { success, result, message } = await postAction('/sys/loginVerifyUsernameAndPassword', {
            username,
            password,
            captcha,
            checkKey
        });
        if(success) {
            await commit({
                type: 'FACTOR',
                payload: { factor: result }
            });
            return { success, result, message };
        } ;
        return toastError(message);
    };

    const smsIn = async (params) => {
        const { factor } = state || {};
        const { success, result, message } = await smsFactorValidate(params);
        const { area, areaList, departs, sysAllDictItems, token, loginSource } = result || {};
        const url = [WEBSITE_ACTIVE_FLAG.False].includes(area?.activeFlag) ? '/under-construction' : (redirectUrl || '/honhai/app');
        if(success) {
            toastSuccess('登錄成功！');
            await setSession(token);
            // navigator([WEBSITE_ACTIVE_FLAG.False].includes(area?.activeFlag) ? '/under-construction' : '/honhai/app', { replace: true });
            await getProfileInformation(factor?.username, loginSource, area, areaList);
            // navigator(url, { replace: true });
            window.open(
                `${process.env.REACT_APP_API_PUBLIC_URL}${url}`,
                '_self'
            );
        } else {
            toastError(message);
        };
    };

    const emailIn = async (params) => {
        const { factor } = state || {};
        const { success, result, message } = await emailFactorValidate(params);
        const { area, areaList, departs, sysAllDictItems, token, loginSource } = result || {};
        const url = [WEBSITE_ACTIVE_FLAG.False].includes(area?.activeFlag) ? '/under-construction' : (redirectUrl || '/honhai/app');
        if(success) {
            toastSuccess('登錄成功！');
            await setSession(token);
            await getProfileInformation(factor?.username, loginSource, area, areaList);
            // navigator(url, { replace: true });
            // Open the appropriate URL
            window.open(
                `${process.env.REACT_APP_API_PUBLIC_URL}${url}`,
                '_self'
            );
        } else {
            toastError(message);
        };
    };

    // 相信授權登錄 Icivet Authorize
    const ssoIn = async () => {
        const urlParams = new URLSearchParams(search);
        const stateParam = urlParams.get('state');
        const oidcNextUrl = getStorage(OIDC_NEXT_URL);
        postRequest(
          '/sys/portal/login-sso',
          {
              ssoCode: urlParams.get('code'),
              ssoType: '1',
              state: stateParam
          },
          (progress) => {}
        ).then(async (res) => {
            const { code, success, message, result } = res || {};
            const { token, area, areaList, userInfo, employeeInfo, loginSource, targetUrl } = result || {};

            const url = [WEBSITE_ACTIVE_FLAG.False].includes(area?.activeFlag) 
                        ? '/under-construction' 
                        : (targetUrl || oidcNextUrl || '/honhai/app');
                
            if (success) {
                dispatch({
                    type: OPEN_ICIVET_AUTHORIZE_DIALOG,
                    payload: false
                });
                await setSession(token);

                await setStorage(USER_NAME, userInfo?.username);
                await setStorage(USER_LOGIN_METHOD, loginSource);
                await setStorage(USER_DEFAULT_AREA, area);
                await setStorage(USER_AREA_LIST, areaList);

                await getProfileInformation(userInfo?.username, loginSource, area, areaList);

                // Open the appropriate URL
                window.open(
                    `${process.env.REACT_APP_API_PUBLIC_URL}${url}`,
                    '_self'
                );
                
                // if(oidcNextUrl) {window.open(decodeURIComponent(oidcNextUrl)) };
                return;
            };

            if([101, '101'].includes(code)) {
                await navigator('/under-construction', { replace: true });
            } else {
                // window.parent?.location?.reload();
                await navigator('/honhai/app', { replace: true });
            };
            toastError(message);
        });
    };

    const logOut = async () => {
        const { success, message } = await postAction('/sys/logout');
        if(success) {
            await setSession(null);
            await resetStore();
            await commit({ type: 'LOGOUT' });
            await dispatch(setUserInformation({}));
            toastSuccess(message);
            await navigator('/honhai/app', { replace: true });
            // window.open(
            //     `${process.env.REACT_APP_API_PUBLIC_URL}${`/honhai/app`}`,
            //     '_self'
            // );
        } else {
            toastError(message);
        }
    };

    // 登出不跳轉
    const signOut = async () => {
        const { success, message } = await postAction('/sys/logout');
        if(success) {
            setSession(null);
            resetStore();
            commit({ type: 'LOGOUT' });
            dispatch(setUserInformation({}));
        };
    };

    const register = async (email, name, password) => {
        const response = await axios.post('/api/account/register', {
            email,
            name,
            password
        });
        const { accessToken, profile } = response;
        commit({
            type: 'REGISTER',
            payload: {
                profile
            }
        });
    };

    // 員工激活註冊
    const signUp = async (workNo, joinDate) => {
        const response = await getAction('/sys/emp/activateUser', {
            workNo,
            joinDate,
            entranceCode: PROJECT.code
        });
        return response;
    };

    // 登錄後獲取首頁數據
    const updateDashboardData = async () => {
        // Banner
        await dispatch(queryDashboardBanner());
        // 為你推薦
        await dispatch(queryRecommend());
        // 友善連結
        await dispatch(queryFriendlyLinks())
    };

    

    return (
      <AuthContext.Provider
        value={{
            ...state,
            signIn,
            smsIn,
            emailIn,
            ssoIn,
            logOut,
            signOut,
            register,
            signUp,
            updateProfileInformation: getProfileInformation
        }}
      >
          {children}
      </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export default AuthContext;
